import { useCallback, useMemo, useState } from 'react'
import { PhoneInfoScreen } from './screens/basic-player-information/phone-player-information-screen'
import { SlotSelection } from './screens/bookable-selection/bookable-selection-screen'
import { ProgressStepper } from '@Components/progress-stepper/progress-stepper'

import './standard-claim-flow.scss'
import { ChooseLocationScreen } from './screens/choose-location/choose-location-screen'
import { useMutation } from '@tanstack/react-query'
import { PlayerDataServiceAPI } from '@API/data-services/player-data-service'
import { useDispatch } from 'react-redux'
import { Header } from '@Components/header/header'
import {
  ClaimReservationCommunicationMethod,
  ClaimReservationResponse,
} from '@interaction-gaming/player-data-service-browser-client'
import { ClaimReservationActions } from '@AppState/claim-reservation'
import { useNavigate } from 'react-router-dom'
import { useHostConfig } from '../../hooks/use-host-config'
import { LoadingIndicator } from '@Components/loading-indicator/loading-indicator'
import { EmailInfoScreen } from './screens/basic-player-information/email-player-information-screen'
import { PlayerInfoFormData } from './screens/basic-player-information/types'

enum FORM_STEPS {
  PLAYER_INFO = 0,
  CHOOSE_LOCATION = 1,
  SLOT_SELECTION = 2,
  PENDING = 3,
}

export type ClaimTicketFormProps = {
  originalReservation: ClaimReservationResponse
}

export default function EditClaimTicketForm({ originalReservation }: ClaimTicketFormProps) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [step, setStep] = useState<FORM_STEPS>(FORM_STEPS.PLAYER_INFO)

  const [playerInfo, setPlayerInfo] = useState<PlayerInfoFormData>({
    firstName: originalReservation.claimReservationDetails.name.split(' ')[0],
    lastName: originalReservation.claimReservationDetails.name.split(' ')[1],
    emailAddress: originalReservation.claimReservationDetails.email || '',
    phoneNumber: originalReservation.claimReservationDetails.phone || '',
    numberOfTickets: originalReservation.claimReservationDetails.numberOfTickets,
  })

  const communicationPreference = useMemo(() => {
    const hasPhone = originalReservation.claimReservationDetails.phone?.length ?? 0 > 0
    const hasEmail = originalReservation.claimReservationDetails.email?.length ?? 0 > 0

    if (hasPhone) {
      return ClaimReservationCommunicationMethod.PHONE
    }

    if (hasEmail) {
      return ClaimReservationCommunicationMethod.EMAIL
    }

    return ClaimReservationCommunicationMethod.PHONE
  }, [originalReservation])
  const [locations, setLocations] = useState<string[]>([])

  const hostConfig = useHostConfig()

  const TITLE_BY_STEP: Record<FORM_STEPS, string> = {
    [FORM_STEPS.PLAYER_INFO]: 'Basic Info',
    [FORM_STEPS.CHOOSE_LOCATION]: 'Choose Location',
    [FORM_STEPS.SLOT_SELECTION]: hostConfig.bookableScreen.header,
    [FORM_STEPS.PENDING]: 'Making Reservation',
  }

  const { mutate: makeClaimReservation } = useMutation({
    mutationFn: async (slotId: string) => {
      return await PlayerDataServiceAPI.editClaimReservation(
        {
          notes: null,
          numberOfTickets: playerInfo.numberOfTickets!,
          phone: playerInfo.phoneNumber,
          email: playerInfo.emailAddress,
          name: `${playerInfo.firstName} ${playerInfo.lastName}`,
          timeslotId: slotId,
          communicationPreference: communicationPreference,
        },
        originalReservation.id,
      )
    },
    onSuccess: (response) => {
      dispatch(ClaimReservationActions.setClaimReservation(response))
      navigate(`/confirm${location.search}`)
    },
  })

  const renderContents = useCallback(() => {
    switch (step) {
      case FORM_STEPS.PLAYER_INFO:
        if (communicationPreference === ClaimReservationCommunicationMethod.PHONE) {
          return (
            <PhoneInfoScreen
              rescheduleFlow={true}
              formData={playerInfo ?? undefined}
              onSubmit={(data: PlayerInfoFormData) => {
                setPlayerInfo(data)
                setStep(FORM_STEPS.CHOOSE_LOCATION)
              }}
              onBack={() => navigate(`/edit${location.search}`)}
            />
          )
        } else if (communicationPreference === ClaimReservationCommunicationMethod.EMAIL) {
          return (
            <EmailInfoScreen
              rescheduleFlow={true}
              formData={playerInfo ?? undefined}
              onSubmit={(data: PlayerInfoFormData) => {
                setPlayerInfo(data)
                setStep(FORM_STEPS.CHOOSE_LOCATION)
              }}
              onBack={() => navigate(`/edit${location.search}`)}
            />
          )
        } else {
          throw new Error('No contact information for reservation')
        }

      case FORM_STEPS.CHOOSE_LOCATION:
        return (
          <ChooseLocationScreen
            initialValue={[originalReservation.claimCenter.id]}
            onNext={(val: string[]) => {
              setLocations(val)
              setStep(FORM_STEPS.SLOT_SELECTION)
            }}
            onBack={() => {
              setStep(FORM_STEPS.PLAYER_INFO)
            }}
          />
        )

      case FORM_STEPS.SLOT_SELECTION:
        return (
          <SlotSelection
            selectedCenters={locations}
            onSubmit={(slot: string) => {
              setStep(FORM_STEPS.PENDING)
              makeClaimReservation(slot)
            }}
            onBack={() => {
              setLocations([])
              setStep(FORM_STEPS.CHOOSE_LOCATION)
            }}
            numberOfTickets={playerInfo.numberOfTickets}
          />
        )

      case FORM_STEPS.PENDING:
        return <LoadingIndicator active={true} />
    }
  }, [
    step,
    locations,
    makeClaimReservation,
    navigate,
    playerInfo,
    originalReservation.claimCenter.id,
    communicationPreference,
  ])

  const renderProgressStepper = useCallback(() => {
    let progressStep
    switch (step) {
      case FORM_STEPS.PLAYER_INFO:
        progressStep = 1
        break
      case FORM_STEPS.CHOOSE_LOCATION:
        progressStep = 2
        break
      case FORM_STEPS.SLOT_SELECTION:
        progressStep = 3
        break
      case FORM_STEPS.PENDING:
        progressStep = 3
        break
    }

    return (
      <ProgressStepper
        steps={[{ label: 'Amount' }, { label: 'Location' }, { label: 'Date' }, { label: 'Info' }]}
        currentStepIndex={progressStep}
      />
    )
  }, [step])

  return (
    <>
      <Header title={'Schedule an Appointment'} />
      <section className="form">
        {renderProgressStepper()}
        <h2 className="form__title">{TITLE_BY_STEP[step]}</h2>
        <section className="form__contents">{renderContents()}</section>
      </section>
    </>
  )
}
